import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

const RISK_BADGE = {
  red:    { label: 'Hög risk',             className: 'bg-red-100 text-red-700' },
  yellow: { label: 'Kräver uppmärksamhet', className: 'bg-yellow-100 text-yellow-700' },
};

const MAX_VISIBLE = 10;

export default function RiskOverview() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [myOnly, setMyOnly]   = useState(false);

  const isAdmin = user?.role === 'customer_admin' || user?.role === 'super_admin';

  useEffect(() => {
    api.get('/dashboard/risk-overview')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <Skeleton className="h-4 w-64 mb-4" />
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.total_at_risk === 0) {
    return (
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="py-4 px-5 flex items-center gap-2 text-sm text-slate-500">
          <CheckCircle size={15} className="text-green-500 shrink-0" />
          Alla deltagare har god progression
        </CardContent>
      </Card>
    );
  }

  const filteredList = (() => {
    let list = data.participants;
    if (myOnly && user?.name) list = list.filter(p => p.assigned_employee === user.name);
    return list;
  })();

  const visibleList  = filteredList.slice(0, MAX_VISIBLE);
  const totalVisible = myOnly && user?.name
    ? data.participants.filter(p => p.assigned_employee === user.name).length
    : data.total_at_risk;

  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardContent className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              DELTAGARE SOM KRÄVER UPPMÄRKSAMHET
            </p>
            {(data.red_count > 0 || data.yellow_count > 0) && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {data.red_count > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    {data.red_count} hög risk
                  </span>
                )}
                {data.yellow_count > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    {data.yellow_count} kräver uppmärksamhet
                  </span>
                )}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="flex gap-1.5 shrink-0">
              {[
                { key: false, label: 'Alla' },
                { key: true,  label: 'Mina deltagare' },
              ].map(f => (
                <button
                  key={String(f.key)}
                  onClick={() => setMyOnly(f.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    myOnly === f.key
                      ? 'bg-slate-800 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {visibleList.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">
            Inga deltagare med identifierade risker i den här vyn.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#f8fafc' }}>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Namn</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Risknivå</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Orsaker</th>
                  <th className="text-right px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-500">Inaktiv</th>
                </tr>
              </thead>
              <tbody>
                {visibleList.map(p => {
                  const badge = RISK_BADGE[p.risk_level] || RISK_BADGE.yellow;
                  return (
                    <tr
                      key={p.participant_id}
                      className="border-b last:border-0 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate(p.profile_url)}
                      data-testid={`risk-row-${p.participant_id}`}
                    >
                      <td className="px-3 py-3 font-semibold text-slate-800">{p.participant_name}</td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-slate-500 text-xs max-w-xs truncate">
                        {p.risk_labels.join(', ') || '–'}
                      </td>
                      <td className="px-3 py-3 text-right text-slate-500 text-xs whitespace-nowrap">
                        {p.days_enrolled}d
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalVisible > MAX_VISIBLE && (
          <button
            className="text-xs font-medium text-slate-600 hover:underline flex items-center gap-1 pt-3"
            onClick={() => navigate('/participants')}
          >
            Visar {MAX_VISIBLE} av {totalVisible} — se alla <ArrowRight size={11} />
          </button>
        )}
      </CardContent>
    </Card>
  );
}
